/**
 * Catálogo de variáveis dos documentos (docs/areas/documentos-e-assinaturas.md, "Catálogo de
 * variáveis"; P-DOC-01). Tudo que entra no texto de um contrato ou termo vem de um destes caminhos.
 *
 * É código, não cadastro: o editor lista o catálogo no menu Variável, o lint recusa caminho fora
 * dele (`invalid_variable`) e a pré-visualização usa `example`. A formatação por tipo e as
 * variáveis derivadas são resolvidas na geração, pela API.
 */

export type VariableGroup =
  | 'patient'
  | 'guardian'
  | 'clinic'
  | 'practitioner'
  | 'appointment'
  | 'anamnesis'
  | 'signature'
  | 'document'

export type VariableType =
  | 'name'
  | 'text'
  /** Texto digitado na geração, que entra como parágrafo e nunca como Markdown (`plainTextToMarkdown`). */
  | 'long_text'
  | 'doc_number'
  | 'phone'
  | 'email'
  | 'date'
  | 'date_time'
  | 'long_date'
  | 'currency'
  | 'integer'
  | 'boolean'
  | 'enum'
  | 'list'
  | 'block'

export interface CatalogVariable {
  /** Caminho escrito no Markdown: `{{patient.name}}`. */
  path: string
  group: VariableGroup
  /** Rótulo para quem escreve, em pt-BR. */
  label: string
  type: VariableType
  /** Calculada pela API na geração; nunca digitada. */
  isDerived: boolean
  /** Sem valor, não bloqueia a geração: entra `fallbackText`. */
  isOptional: boolean
  fallbackText: string | null
  /** Valor de exemplo, já como sai no documento, para a pré-visualização. */
  example: string
}

export const VARIABLE_GROUP_LABELS: Record<VariableGroup, string> = {
  patient: 'Paciente',
  guardian: 'Responsável',
  clinic: 'Clínica',
  practitioner: 'Prestador',
  appointment: 'Atendimento',
  anamnesis: 'Anamnese',
  signature: 'Assinatura',
  document: 'Documento',
}

interface Extra {
  derived?: boolean
  fallback?: string
}

function variable(
  path: string,
  label: string,
  type: VariableType,
  example: string,
  extra: Extra = {},
): CatalogVariable {
  return {
    path,
    group: path.slice(0, path.indexOf('.')) as VariableGroup,
    label,
    type,
    isDerived: extra.derived ?? false,
    isOptional: extra.fallback !== undefined,
    fallbackText: extra.fallback ?? null,
    example,
  }
}

const derived = { derived: true } as const

export const VARIABLE_CATALOG: readonly CatalogVariable[] = [
  // Paciente (cadastro)
  variable('patient.name', 'Nome', 'name', 'Amanda Lima'),
  variable('patient.docNumber', 'CPF', 'doc_number', '111.444.777-35'),
  variable('patient.docType', 'Tipo do documento', 'enum', 'CPF', derived),
  variable('patient.foreignDocument', 'Documento estrangeiro', 'text', 'Passaporte AB123456'),
  variable('patient.idDocument', 'Documento com rótulo', 'text', 'CPF 111.444.777-35', derived),
  variable('patient.rg', 'RG', 'text', '12.345.678-9', { fallback: 'não informado' }),
  variable('patient.birthDate', 'Data de nascimento', 'date', '14/03/1993'),
  variable('patient.age', 'Idade', 'integer', '33', derived),
  variable('patient.isMinor', 'Menor de idade', 'boolean', 'no', derived),
  variable('patient.isForeign', 'Estrangeira', 'boolean', 'no', derived),
  variable('patient.maritalStatus', 'Estado civil', 'text', 'solteira', {
    fallback: 'não informado',
  }),
  variable('patient.occupation', 'Profissão', 'text', 'autônoma', { fallback: 'não informada' }),
  variable('patient.email', 'E-mail', 'email', 'amanda.lima@gmail.com', {
    fallback: 'não informado',
  }),
  variable('patient.whatsapp', 'WhatsApp', 'phone', '(11) 99686-0918'),
  variable('patient.address.postalCode', 'CEP', 'text', '01400-000'),
  variable('patient.address.street', 'Rua', 'text', 'Rua das Flores'),
  variable('patient.address.number', 'Número', 'text', '123'),
  variable('patient.address.complement', 'Complemento', 'text', 'apto 4', { fallback: '' }),
  variable('patient.address.district', 'Bairro', 'text', 'Jardins'),
  variable('patient.address.city', 'Cidade', 'text', 'São Paulo'),
  variable('patient.address.state', 'UF', 'text', 'SP'),
  variable(
    'patient.fullAddress',
    'Endereço completo',
    'text',
    'Rua das Flores, 123, apto 4, Jardins, São Paulo/SP, CEP 01400-000',
    derived,
  ),
  variable('patient.hasGuardian', 'Tem responsável', 'boolean', 'no', derived),

  // Responsável (paciente menor de idade)
  variable('guardian.name', 'Nome', 'name', 'Marina Lima'),
  variable('guardian.docNumber', 'CPF', 'doc_number', '112.334.556-78'),
  variable('guardian.docType', 'Tipo do documento', 'enum', 'CPF', derived),
  variable('guardian.whatsapp', 'WhatsApp', 'phone', '(11) 98888-0000'),

  // Clínica (Empresa)
  variable('clinic.legalName', 'Razão social', 'text', 'Westhetics Experience Ltda'),
  variable('clinic.docNumber', 'CNPJ ou CPF', 'doc_number', '01.996.766/0001-30'),
  variable('clinic.idDocument', 'Documento com rótulo', 'text', 'CNPJ 01.996.766/0001-30', derived),
  variable('clinic.docType', 'Tipo do documento', 'enum', 'CNPJ', derived),
  variable(
    'clinic.legalRepresentative.name',
    'Responsável legal',
    'name',
    'Gabriel Ribeiro Marques',
  ),
  variable(
    'clinic.legalRepresentative.docNumber',
    'CPF da responsável legal',
    'doc_number',
    '123.456.789-09',
  ),
  variable('clinic.legalRepresentative.docType', 'Tipo do documento', 'enum', 'CPF', derived),
  variable('clinic.hasLegalRepresentative', 'Tem responsável legal', 'boolean', 'yes', derived),
  variable('clinic.address.postalCode', 'CEP', 'text', '08035-020'),
  variable('clinic.address.street', 'Rua', 'text', 'Rua Curuçá'),
  variable('clinic.address.number', 'Número', 'text', '120'),
  variable('clinic.address.complement', 'Complemento', 'text', 'sala 2', { fallback: '' }),
  variable('clinic.address.district', 'Bairro', 'text', 'Vila Curuçá'),
  variable('clinic.address.city', 'Cidade', 'text', 'São Paulo'),
  variable('clinic.address.state', 'UF', 'text', 'SP'),
  variable(
    'clinic.fullAddress',
    'Endereço completo',
    'text',
    'Rua Curuçá, 120, sala 2, Vila Curuçá, São Paulo/SP, CEP 08035-020',
    derived,
  ),
  // Testemunhas como no legado (decisão de 16/10/2026): opcionais, nunca impedem a geração; sem
  // testemunha, a linha de assinatura sai em branco para preencher à mão.
  variable('clinic.witnesses[1].name', 'Primeira testemunha', 'name', 'Gabriel Ribeiro Marques', {
    fallback: '',
  }),
  variable(
    'clinic.witnesses[1].docNumber',
    'CPF da primeira testemunha',
    'doc_number',
    '123.456.789-09',
    { fallback: '' },
  ),
  variable('clinic.witnesses[1].docType', 'Tipo do documento', 'enum', 'CPF', {
    derived: true,
    fallback: '',
  }),
  variable('clinic.witnesses[2].name', 'Segunda testemunha', 'name', 'Luciana Gabriela Cirino', {
    fallback: '',
  }),
  variable(
    'clinic.witnesses[2].docNumber',
    'CPF da segunda testemunha',
    'doc_number',
    '529.982.247-25',
    { fallback: '' },
  ),
  variable('clinic.witnesses[2].docType', 'Tipo do documento', 'enum', 'CPF', {
    derived: true,
    fallback: '',
  }),
  variable('clinic.hasWitnesses', 'Tem as duas testemunhas', 'boolean', 'yes', derived),

  // Prestador (a profissional responsável pelo atendimento)
  variable('practitioner.name', 'Nome', 'name', 'Camila R. de Castro'),
  variable('practitioner.profession', 'Profissão', 'text', 'Médica'),
  variable('practitioner.specialty', 'Especialidade', 'text', 'Dermatologia', { fallback: '' }),
  variable('practitioner.council', 'Conselho', 'text', 'CRM'),
  variable('practitioner.registrationNumber', 'Registro', 'text', 'SP 88998'),
  variable('practitioner.fullRegistration', 'Conselho e registro', 'text', 'CRM-SP 88998', derived),
  variable('practitioner.license', 'Habilitação', 'text', 'CRM-SP 88998', derived),

  // Atendimento
  variable(
    'appointment.procedures',
    'Procedimentos',
    'list',
    'Preenchimento labial e Toxina botulínica',
  ),
  variable('appointment.procedureCount', 'Quantidade de procedimentos', 'integer', '2', derived),
  variable('appointment.totalAmount', 'Valor total', 'currency', 'R$ 3.000,00'),
  variable(
    'appointment.totalAmountInWords',
    'Valor por extenso',
    'text',
    'três mil reais',
    derived,
  ),
  variable(
    'appointment.paymentTerms',
    'Condição de pagamento',
    'text',
    'PIX, débito ou crédito em até 10× sem juros.',
  ),
  variable('appointment.procedureDate', 'Data do procedimento', 'date', '05/09/2026', {
    fallback: 'a combinar',
  }),
  variable('appointment.hasProcedureDate', 'Tem data marcada', 'boolean', 'yes', derived),
  variable('appointment.imageConsent', 'Uso de imagem', 'enum', 'Autoriza'),
  // Digitado na etapa 2 quando um documento escolhido o usa (RN-DOC-52); vale para toda a geração.
  variable(
    'appointment.freeText',
    'Texto livre',
    'long_text',
    'A paciente relata sensibilidade nos lábios e prefere começar com uma dose menor, com retorno em 15 dias.',
  ),
  variable('appointment.isModelPatient', 'Paciente modelo', 'boolean', 'no', derived),
  variable('appointment.hasAnamnesis', 'Tem anamnese', 'boolean', 'yes', derived),

  // Anamnese usada como base
  variable('anamnesis.completedAt', 'Respondida em', 'date_time', '28/04/2026 · 09:51', derived),
  variable('anamnesis.protocolNumber', 'Protocolo', 'text', 'WX-4471-AL', derived),
  variable('anamnesis.hash', 'Hash', 'text', 'a3f9c1e7…4b82d5', derived),
  variable('anamnesis.answers', 'Respostas', 'block', '', derived),

  // Assinatura e documento
  variable('signature.date', 'Data por extenso', 'long_date', '28 de setembro de 2026', derived),
  variable('signature.place', 'Cidade', 'text', 'São Paulo', derived),
  variable(
    'signature.placeAndDate',
    'Local e data',
    'text',
    'São Paulo, 28 de setembro de 2026',
    derived,
  ),
  variable('document.version', 'Versão', 'text', '2026.08', derived),
  variable('document.generatedAt', 'Gerado em', 'date', '28/09/2026', derived),
]

const BY_PATH = new Map(VARIABLE_CATALOG.map((v) => [v.path, v]))

/** A entrada do catálogo para um caminho, ou `undefined` quando ele não existe. */
export function getCatalogVariable(path: string): CatalogVariable | undefined {
  return BY_PATH.get(path)
}

/** Verdadeiro quando o caminho está no catálogo. */
export function isCatalogVariable(path: string): boolean {
  return BY_PATH.has(path)
}

/**
 * Valores de exemplo de todo o catálogo, para a pré-visualização do editor
 * (`{ 'patient.name': 'Amanda Lima', … }`). Variável de bloco não entra: só existe por diretiva.
 */
export function catalogSampleValues(): Record<string, string> {
  const out: Record<string, string> = {}
  for (const v of VARIABLE_CATALOG) if (v.type !== 'block') out[v.path] = v.example
  return out
}

/**
 * Caminho parecido que existe no catálogo, para a mensagem do lint ("Use patient.whatsapp").
 * Compara pelo último trecho do caminho e pelo grupo.
 */
export function suggestCatalogVariable(path: string): string | undefined {
  const group = path.slice(0, Math.max(0, path.indexOf('.')))
  const leaf = path.slice(path.lastIndexOf('.') + 1).toLowerCase()
  const ALIASES: Record<string, string> = {
    phone: 'whatsapp',
    cpf: 'docNumber',
    cnpj: 'docNumber',
    address: 'fullAddress',
    registration: 'registrationNumber',
    date: 'procedureDate',
    amount: 'totalAmount',
    value: 'totalAmount',
  }
  const wanted = (ALIASES[leaf] ?? leaf).toLowerCase()
  const sameGroup = VARIABLE_CATALOG.filter((v) => v.group === group)
  const pool = sameGroup.length ? sameGroup : VARIABLE_CATALOG
  return pool.find((v) => v.path.slice(v.path.lastIndexOf('.') + 1).toLowerCase() === wanted)?.path
}

/**
 * O público do documento (docs/areas/documentos-e-assinaturas.md, RN-DOC-42): para quem ele é
 * escrito. A pessoa que assina é sempre o grupo `patient` do catálogo, em qualquer público (P-DOC-30);
 * o que muda é o rótulo que quem escreve lê.
 */
export type DocumentAudience = 'patient' | 'student' | 'employee' | 'third_party'

/** Como se chama, no singular, a pessoa que assina em cada público. */
const PERSON_NOUNS: Record<DocumentAudience, string> = {
  patient: 'paciente',
  student: 'aluno',
  employee: 'funcionário',
  third_party: 'terceiro',
}

/**
 * Os rótulos da pessoa que não seguem a regra "<rótulo> do aluno": o documento aceita CNPJ fora de
 * paciente (`pacientes.md` RN-PAC-38) e os sim ou não leem melhor com o nome na frente.
 */
const PERSON_LABELS: Record<string, (noun: string) => string> = {
  'patient.docNumber': (noun) => `CPF ou CNPJ do ${noun}`,
  'patient.isMinor': (noun) => `${capitalize(noun)} menor de idade`,
  'patient.isForeign': (noun) => `${capitalize(noun)} estrangeiro`,
  'patient.hasGuardian': (noun) => `${capitalize(noun)} tem responsável`,
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/**
 * O nome do grupo `patient` no menu Variável, pelo público do documento: "Paciente", "Aluno",
 * "Funcionário", "Terceiro". Os outros grupos não mudam.
 */
export function catalogGroupLabel(group: VariableGroup, audience: DocumentAudience = 'patient') {
  return group === 'patient' ? capitalize(PERSON_NOUNS[audience]) : VARIABLE_GROUP_LABELS[group]
}

/**
 * O rótulo da variável para quem escreve um documento deste público: no público `patient`, o do
 * catálogo; nos outros, o da pessoa pelo nome dela ("Nome do aluno", "CPF ou CNPJ do terceiro").
 * O caminho não muda: `{{patient.name}}` vale em todo público (P-DOC-30).
 */
export function catalogVariableLabel(
  variable: CatalogVariable,
  audience: DocumentAudience = 'patient',
): string {
  if (variable.group !== 'patient' || audience === 'patient') return variable.label
  const noun = PERSON_NOUNS[audience]
  const special = PERSON_LABELS[variable.path]
  return special ? special(noun) : `${variable.label} do ${noun}`
}
